import React, { useMemo } from "react";
import { BaseEdge, MarkerType, useStore } from "@xyflow/react";
import { buildOrthogonalEdgePath } from "../../utils/edgeRouting";

// Rendered by the dynamic workflow tab: an orthogonal edge that routes around the other nodes
// instead of running underneath them.

const PADDING = 10;

const rectOf = (node) => {
  const position = node?.internals?.positionAbsolute ?? node?.positionAbsolute ?? node?.position;
  const width = node?.measured?.width ?? node?.width ?? 0;
  const height = node?.measured?.height ?? node?.height ?? 0;

  if (!position || !width || !height) return null;

  return {
    left: position.x - PADDING,
    right: position.x + width + PADDING,
    top: position.y - PADDING,
    bottom: position.y + height + PADDING,
  };
};

/**
 * An edge that avoids the node boxes.
 *
 * @param {object} props React Flow edge props.
 */
const LANE_GAP = 16;

// Colours are assigned by the source node's order (top-to-bottom, left-to-right) so adjacent
// origins always differ, and the palette alternates warm/cool for maximum separation. Beyond one
// cycle the dash pattern changes too, so even a repeated hue is still distinguishable.
const EDGE_COLORS = [
  "#ff6b6b", // red
  "#4dabf7", // blue
  "#51cf66", // green
  "#ffa94d", // orange
  "#b197fc", // violet
  "#3bc9db", // cyan
  "#ffd43b", // yellow
  "#f783ac", // pink
  "#a9e34b", // lime
  "#da77f2", // orchid
];

const DASH_PATTERNS = [undefined, "10 6", "2 5", "14 5 2 5"];

export const OrthogonalEdge = ({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  markerEnd,
  markerStart,
  style,
  selected,
}) => {
  const nodeLookup = useStore((state) => state.nodeLookup);
  const storeEdges = useStore((state) => state.edges);

  const rects = useMemo(() => {
    if (!nodeLookup) return [];

    const obstacles = [];

    nodeLookup.forEach((node, nodeId) => {
      if (nodeId === source || nodeId === target) return;
      const rect = rectOf(node);
      if (rect) obstacles.push(rect);
    });

    return obstacles;
  }, [nodeLookup, source, target]);

  // Edges sharing a handle are given separate lanes, ordered by where they come from / go to,
  // so several edges converging on one input don't collapse into a single line.
  const laneOffset = (group, index) =>
    group.length > 1 ? (index - (group.length - 1) / 2) * LANE_GAP : 0;

  const { entryOffset, exitOffset, entryLane, exitLane } = useMemo(() => {
    const edges = storeEdges ?? [];
    const yOf = (nodeId) => {
      const node = nodeLookup?.get?.(nodeId);
      const position = node?.internals?.positionAbsolute ?? node?.positionAbsolute ?? node?.position;
      return position?.y ?? 0;
    };

    const byId = (a, b) => String(a.id).localeCompare(String(b.id));

    // Group by node (not handle): edges into different handles of one node still need separate
    // corridors, otherwise their vertical runs coincide and the origins are ambiguous.
    const entryGroup = edges
      .filter((edge) => edge.target === target)
      .sort((a, b) => yOf(a.source) - yOf(b.source) || byId(a, b));

    const exitGroup = edges
      .filter((edge) => edge.source === source)
      .sort((a, b) => yOf(a.target) - yOf(b.target) || byId(a, b));

    const entryIndex = Math.max(0, entryGroup.findIndex((edge) => edge.id === id));
    const exitIndex = Math.max(0, exitGroup.findIndex((edge) => edge.id === id));

    return {
      entryOffset: laneOffset(entryGroup, entryIndex),
      exitOffset: laneOffset(exitGroup, exitIndex),
      entryLane: entryIndex,
      exitLane: exitIndex,
    };
  }, [storeEdges, nodeLookup, id, source, target]);

  // Order the sources the way they appear on the canvas, then give each a palette slot.
  const sourceOrder = useMemo(() => {
    const positionOf = (nodeId) => {
      const node = nodeLookup?.get?.(nodeId);
      const position = node?.internals?.positionAbsolute ?? node?.positionAbsolute ?? node?.position;
      return { x: position?.x ?? 0, y: position?.y ?? 0 };
    };

    const ids = Array.from(new Set((storeEdges ?? []).map((edge) => edge.source)));

    return ids.sort((a, b) => {
      const left = positionOf(a);
      const right = positionOf(b);
      return left.y - right.y || left.x - right.x || String(a).localeCompare(String(b));
    });
  }, [storeEdges, nodeLookup]);

  const colorIndex = Math.max(0, sourceOrder.indexOf(source));
  const color = EDGE_COLORS[colorIndex % EDGE_COLORS.length];
  const strokeDasharray =
    DASH_PATTERNS[Math.floor(colorIndex / EDGE_COLORS.length) % DASH_PATTERNS.length];

  const { path } = useMemo(
    () =>
      buildOrthogonalEdgePath({
        sourceX,
        sourceY,
        targetX,
        targetY,
        rects,
        entryOffset,
        exitOffset,
        entryLane,
        exitLane,
      }),
    [sourceX, sourceY, targetX, targetY, rects, entryOffset, exitOffset, entryLane, exitLane]
  );

  return (
    <BaseEdge
      id={id}
      path={path}
      markerEnd={{ type: MarkerType.ArrowClosed, color, width: 16, height: 16 }}
      markerStart={markerStart}
      style={{
        ...style,
        stroke: color,
        strokeWidth: selected ? 2.6 : 2,
        strokeDasharray,
      }}
    />
  );
};

export default OrthogonalEdge;
